<script setup lang="ts">
    import { ref, watch } from 'vue';
    import { useDropzone } from 'vue3-dropzone';
    import standingSagittalImg from '@/assets/images/standing-sagittal.svg';
    import seatedSagittalImg from '@/assets/images/seated-sagittal.svg';
    import lumbarSagittalImg from '@/assets/images/lumbar-sagittal.svg';

    const props = defineProps({
        hipSpinePosition: { type: String, required: true }
    });
    const emit = defineEmits(['addFile'])
    const selectedFile = ref<File | null>(null);
    const mouseOver = ref(false);
    const toggleMouseOver = (on: boolean) => () => {
        mouseOver.value = on;
        return true;
    };

    const onDrop = (files: File[]) => {
        if (files && files.length === 1) {
            selectedFile.value = files[0];
        }
    }

    const dropzone = useDropzone({
        multiple: false,
        onDrop,
        onDragEnter: toggleMouseOver(true),
        onDragLeave: toggleMouseOver(false),
        onDropAccepted: toggleMouseOver(false),
        onDropRejected: toggleMouseOver(false),
    });

    watch(selectedFile, () => {
        if (selectedFile.value) {
            emit('addFile', selectedFile.value);
        }
    })

    const sagittalPositionImg = () => {
        switch (props.hipSpinePosition) {
            case "Standing Sagittal":
                return standingSagittalImg;
            case "Seated Sagittal":
                return seatedSagittalImg;
            case "Lumbar Sagittal":
                return lumbarSagittalImg;
        }
    }

</script>

<template>
    <div class="drag-dicom-container" v-bind="dropzone.getRootProps()">
        <input v-bind="dropzone.getInputProps()" />

        <div class="drag-subcontainer">

            <img :src="sagittalPositionImg()" alt="Hip Spine Position Image" />

            <div>
                <p class="input-title mb-0">{{ props.hipSpinePosition }}</p>
                <p class="input-subtitle">
                    {{ props.hipSpinePosition === "Standing Sagittal" ? "REQUIRED" : "SUPPLEMENTAL" }}</p>
                <div v-if="selectedFile" class="override-notice">
                    <p class="filename">{{ selectedFile.name }}</p>
                    <div class="uploaded-text">
                        <v-icon icon="mdi-check-circle-outline" color="success" size="20"></v-icon>
                        <app-text class="mb-0 font-weight-bold">Selected</app-text>
                    </div>
                </div>
                <div v-else class="override-notice">
                    <v-icon icon="mdi-cloud-upload" color="primary" size="40"></v-icon>
                    <app-text class="mb-0 font-weight-bold text-center">Drag and drop your X-ray file here OR click to
                        select a file
                    </app-text>
                </div>
                <app-button-secondary>{{
                        selectedFile ? "Select New X-ray" : "Click to select"
                    }}
                </app-button-secondary>
            </div>

        </div>
    </div>
</template>

<style scoped lang="scss">
    .drag-dicom-container {
        cursor: pointer;
        width: 100%;
        height: 100%;
        border-radius: 4px;
        border-width: 1px;
        border-style: dashed;
        border-color: rgb(var(--v-theme-outline));
        font-size: 18px;
        transition: border 200ms;
    }

    .drag-subcontainer {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 40px 10px;
    }

    img {
        width: 176px;
        height: 176px;
    }

    .drag-subcontainer > div {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
    }

    .override-notice {
        margin: 16px 0;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
    }

    .override-notice > div {
        display: flex;
        flex-direction: row;
        gap: 6px;
    }

    .input-subtitle, .filename {
        font-size: 16px;
        color: rgb(var(--v-theme-secondary-text));
    }

    .input-title {
        font-size: 21px;
        font-weight: 700;
        color: rgb(var(--v-theme-secondary-text));
    }

    @media screen and (max-width: 1000px) {
        .drag-subcontainer {
            flex-direction: column;
        }

        img {
            width: 120px;
            height: 120px;
        }
    }
</style>